import { useState } from "react";
import type { ElementRef } from "../model/inp/properties.ts";
import { mergeSelection, sameElementRef } from "../model/inp/selection.ts";

export type ResultsDashboardLayout = "combined" | "grid";
export type ResultsDashboardView = "chart" | "table";

// `elements` is deliberately its own state, separate from App.tsx's
// selected/multiSelected: if the dashboard shared that selection, clearing
// it (Escape, background click) or deleting the selection elsewhere would
// silently empty the dashboard's chart too. App.tsx's "Add selection"
// button instead copies the current selection in via `addElements`.
export function useResultsDashboard() {
  const [open, setOpen] = useState(false);
  const [elements, setElements] = useState<ElementRef[]>([]);
  const [layout, setLayout] = useState<ResultsDashboardLayout>("combined");
  const [view, setView] = useState<ResultsDashboardView>("chart");

  function toggle() {
    setOpen((o) => !o);
  }

  function close() {
    setOpen(false);
  }

  function addElements(refs: ElementRef[]) {
    setElements((current) => mergeSelection(current, refs, true));
  }

  function removeElement(ref: ElementRef) {
    setElements((current) => current.filter((r) => !sameElementRef(r, ref)));
  }

  function clear() {
    setElements([]);
  }

  // Called on project change — elements referenced by name may not exist,
  // or may name something unrelated, in the new project.
  function reset() {
    setOpen(false);
    setElements([]);
    setLayout("combined");
    setView("chart");
  }

  return {
    open,
    elements,
    layout,
    view,
    toggle,
    close,
    addElements,
    removeElement,
    clear,
    setLayout,
    setView,
    reset,
  };
}
